import * as React from "react";
import { AdminCancellationDialog } from "@/components/orders/AdminCancellationDialog";
import { canStartAdminCancellation, useAdminOrderCancellation } from "@/hooks/useAdminOrderCancellation";
import { toUserFacingErrorMessage } from "@/lib/errors";
import { useQuery, useMutation, useAction } from "convex/react";
import type { ColumnDef, Row } from "@tanstack/react-table";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Calendar,
  Check,
  Copy,
  Download,
  Edit3,
  ExternalLink,
  Link2,
  Loader2,
  Mail,
  RefreshCw,
  Split,
  XCircle,
} from "lucide-react";

import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { DataTableFloatingBar } from "@/components/data-table/data-table-floating-bar";
import { InternalEntityDataTable } from "@/components/internal/shared/InternalEntityDataTable";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { BADGE_TONES, pickAssortedBadgeClass } from "@/lib/badge-tones";
import { api } from "@/lib/convexApi";
import { formatCurrency, formatDeliveryMethod, formatStatusLabel } from "@/lib/format";
import {
  exportToCSV,
  formatDateForExport,
  formatCurrencyForExport,
  type ExportColumn,
} from "@/lib/export";
import {
  getOrderStatusBadgeClass,
  getPaymentStatusBadgeClass,
} from "@/lib/status-tag-colors";
import {
  getEnterpriseBadgeClass,
  getEnterpriseBadgeLabel,
  getOrderSequenceBadgeClass,
  getOrderSequenceLabel,
  isDisplayableSequence,
} from "@/lib/customer-tag-colors";
import { isHiddenInInternalOrderTablesByDefault } from "@/lib/order-status-visibility";
import { matchesScopedFilter, parseDateQueryParam } from "./adminTabFilterUtils";
import { useAuthIdentity } from "@/contexts/UserProfileContext";
import { CreditKeyCaptureQueue } from "@/components/internal/credit-key/CreditKeyCaptureQueue";
import { hasPermission } from "@/lib/roles";
import { AdminAmendmentModal } from "./AdminAmendmentModal";
import { FailedPaymentInstallmentConversionDialog } from "./FailedPaymentInstallmentConversionDialog";

interface OrdersTabProps {
  authUserId?: string;
  orderScope?: "orders" | "abandoned" | "both";
  showSplitPaymentStatusColumn?: boolean;
}

interface SplitPaymentMethodOption {
	_id: string;
	type: "card" | "ach";
	isDefault: boolean;
	brand: string | null;
	last4: string | null;
	bankName: string | null;
	displayName: string | null;
}

interface LinkedCreditPaymentMethodSnapshot {
	paymentMethodId: string;
	type: string;
	last4: string | null;
	brand: string | null;
	bankName: string | null;
}

type PaymentLinkMethod = "card" | "us_bank_account";

type FullOrderStatus =
  | "checkout_initiated"
  | "pending_payment"
  | "payment_processing"
  | "awaiting_inventory"
  | "ready_to_prepare"
  | "ready_for_pickup"
  | "in_transit"
  | "delivered"
  | "on_hold"
  | "cancelled";

const ABANDONED_ORDER_STATUSES = new Set([
  "checkout_initiated",
]);

const ORDER_STATUS_TRANSITIONS: Record<FullOrderStatus, FullOrderStatus[]> = {
  checkout_initiated: ["pending_payment", "awaiting_inventory", "ready_to_prepare", "cancelled", "on_hold"],
  pending_payment: ["payment_processing", "awaiting_inventory", "ready_to_prepare", "cancelled", "on_hold"],
  payment_processing: ["awaiting_inventory", "ready_to_prepare", "pending_payment", "cancelled", "on_hold"],
  awaiting_inventory: ["ready_to_prepare", "cancelled", "on_hold"],
  ready_to_prepare: ["ready_for_pickup", "cancelled", "on_hold"],
  ready_for_pickup: ["in_transit", "delivered", "ready_to_prepare", "cancelled", "on_hold"],
  in_transit: ["delivered", "ready_for_pickup", "on_hold"],
  delivered: [],
  on_hold: [
    "pending_payment",
    "payment_processing",
    "awaiting_inventory",
    "ready_to_prepare",
    "ready_for_pickup",
    "in_transit",
    "cancelled",
  ],
  cancelled: [],
};

const ADMIN_STATUS_ACTION_TARGETS = new Set<FullOrderStatus>([
  "awaiting_inventory",
  "ready_to_prepare",
  "on_hold",
]);


const SPECULATIVE_CARGO_STATUS_VALUES = new Set(["at_origin", "in_transit"]);

interface OrderItemRow {
  _id?: string;
  productId?: string;
  cargoId?: string;
  quantity?: number;
  unitPrice?: number;
  unitWeight?: number;
  product?: {
    name?: string;
    slug?: string | null;
  } | null;
  cargo?: {
    ociNumber?: string;
    contractId?: string;
    cargoUuid?: string;
    status?: string;
  } | null;
}

interface InventoryRecheckLineItemImpact {
  cargoId: string;
  deductedBags: number;
  refundedBags: number;
  netAvailableChange: number;
}

interface InventoryRecheckOrderImpact {
  orderId: string;
  deductedBags: number;
  refundedBags: number;
  netAvailableChange: number;
  lastChangedAt: number | null;
  lineItemImpacts: InventoryRecheckLineItemImpact[];
}

interface InventoryRecheckSessionSummary {
  sessionId: string;
  status: "active";
  startedAt: number;
  expiresAt: number;
  startedByName: string | null;
  warehouseId: string | null;
  warehouseName: string | null;
  cargoCount: number;
  impactedOrderCount: number;
  orderDeltaBags: number;
  currentDeltaBags: number;
}

interface InventoryRecheckOrderImpactResponse {
  session: {
    sessionId: string;
    startedAt: number;
    expiresAt: number;
    warehouseId: string | null;
    warehouseName: string | null;
  } | null;
  impacts: InventoryRecheckOrderImpact[];
}

interface SplitPaymentSummary {
  isSplit: true;
  completedInstallments: number;
  totalInstallments: number;
  pendingInstallments: number;
  processingInstallments: number;
  failedInstallments: number;
  nextDueAt: number | null;
}

interface OrderRow {
  id: string;
  orderNumber: string;
  customer: string;
  email: string;
  company: string;
  status: string;
  holdReason: string | null;
  paymentStatus: string;
  splitPaymentSummary: SplitPaymentSummary | null | undefined;
  paymentRecoveryState: "pay_now_recovery" | "none";
  canRecoverPendingPayment: boolean;
  totalAmount: number;
  quantity: number;
  deliveryMethod: string;
  paymentMethod: string;
  isEnterprise: boolean;
  createdAt: string;
  paymentLinkUrl: string;
  items: OrderItemRow[];
  customerOrderSequence: number | null;
  inventoryRecheckImpact: InventoryRecheckOrderImpact | null;
  rawOrder: any;
}

function buildCustomInstallmentDrafts(
  preview: Array<{
    installmentNumber: number;
    amount: number;
    days: number;
    dueDate: string;
  }>,
) {
  return preview.map((installment) => {
    const dateObj = new Date(installment.dueDate);
    const year = dateObj.getUTCFullYear();
    const month = String(dateObj.getUTCMonth() + 1).padStart(2, "0");
    const day = String(dateObj.getUTCDate()).padStart(2, "0");

    return {
      installmentNumber: installment.installmentNumber,
      amount: installment.amount,
      daysAfterOrder: installment.days,
      dueDate: `${year}-${month}-${day}`,
    };
  });
}

function formatDateOnlyForDisplay(dateOnly: string) {
  const [year, month, day] = dateOnly.split("-").map(Number);
  if (!year || !month || !day) return dateOnly;

  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString("en-US", {
    timeZone: "UTC",
  });
}

function hasFailedPayment(order: OrderRow) {
  return ["payment_failed", "payment_cancelled"].includes(order.paymentStatus);
}

function hasFailedCardPayment(order: OrderRow) {
  return hasFailedPayment(order) && order.paymentMethod.toLowerCase().includes("card");
}

const paymentMethodFilterOptions = [
  { label: "ACH / Bank Account", value: "us_bank_account" },
  { label: "Card", value: "card" },
  { label: "Credit (Card)", value: "credit (card)" },
  { label: "Credit (Bank)", value: "credit (bank)" },
  { label: "Credit", value: "credit" },
  { label: "PayPal", value: "paypal" },
  { label: "Wire", value: "wire" },
  { label: "Check", value: "check" },
  { label: "Cheque", value: "cheque" },
  { label: "Cash", value: "cash" },
] as const;

function getDeliveryMethodBadgeClass(deliveryMethod: unknown): string {
  const normalized = String(deliveryMethod ?? "").trim().toLowerCase();
  if (!normalized) return BADGE_TONES.slate;
  if (normalized.includes("pickup")) return BADGE_TONES.sky;
  if (normalized.includes("delivery") || normalized.includes("ship")) return BADGE_TONES.violet;
  if (normalized.includes("freight")) return BADGE_TONES.cyan;
  return BADGE_TONES.slate;
}

function formatPaymentMethodLabel(method: string): string {
  const m = method.toLowerCase();
  if (m === "us_bank_account" || m === "ach") return "ACH";
  if (m === "credit (card)") return "Credit (Card)";
  if (m === "credit (bank)") return "Credit (ACH)";
  if (m === "credit") return "Credit";
  if (m === "card") return "Card";
  if (m === "paypal") return "PayPal";
  if (m === "cheque" || m === "check") return "Check";
  if (m === "wire") return "Wire";
  if (m === "cash") return "Cash";
  return method.charAt(0).toUpperCase() + method.slice(1);
}

function getPaymentMethodBadgeClass(paymentMethod: unknown): string {
  // Payment method badges use neutral / muted tones to avoid clashing
  // with the colorful delivery method badges in the same table row.
  const normalized = String(paymentMethod ?? "").trim().toLowerCase();
  if (!normalized) return BADGE_TONES.slate;
  if (normalized === "credit (card)" || normalized === "credit (bank)" || normalized === "credit")
    return BADGE_TONES.zinc;
  if (normalized.includes("card")) return BADGE_TONES.slate;
  if (normalized.includes("bank") || normalized.includes("ach") || normalized === "us_bank_account")
    return BADGE_TONES.zinc;
  if (normalized.includes("paypal")) return BADGE_TONES.orange;
  if (normalized.includes("wire")) return BADGE_TONES.zinc;
  if (normalized.includes("check") || normalized.includes("cheque")) return BADGE_TONES.amber;
  if (normalized.includes("cash")) return BADGE_TONES.slate;
  return BADGE_TONES.muted;
}

function getPaymentRecoveryBadgeClass(recoveryState: unknown): string {
  return String(recoveryState ?? "").trim().toLowerCase() === "pay_now_recovery"
    ? BADGE_TONES.amber
    : BADGE_TONES.slate;
}

function getSplitPaymentBadgeClass(summary: SplitPaymentSummary): string {
  if (summary.failedInstallments > 0) return BADGE_TONES.red;
  if (summary.completedInstallments >= summary.totalInstallments) return BADGE_TONES.emerald;
  if (summary.processingInstallments > 0) return BADGE_TONES.sky;
  return BADGE_TONES.amber;
}

function normalizeCargoStatusValue(value: unknown): string {
  return String(value ?? "").trim().toLowerCase().replace(/[\s-]+/g, "_");
}

function getOrderStatusValue(order: Pick<OrderRow, "status" | "rawOrder">): FullOrderStatus | null {
  const normalized = String(order.status ?? order.rawOrder?.status ?? "").trim().toLowerCase();
  return Object.prototype.hasOwnProperty.call(ORDER_STATUS_TRANSITIONS, normalized)
    ? (normalized as FullOrderStatus)
    : null;
}

function orderHasSpeculativeCargo(order: OrderRow): boolean {
  return order.items.some((item) => {
    const status = normalizeCargoStatusValue(item.cargo?.status);
    return SPECULATIVE_CARGO_STATUS_VALUES.has(status);
  });
}

function canShowAdminStatusTarget(order: OrderRow, target: FullOrderStatus): boolean {
  if (target === "awaiting_inventory") return orderHasSpeculativeCargo(order);
  if (target === "ready_to_prepare") return !orderHasSpeculativeCargo(order);
  return true;
}

function getAdminStatusActionTargets(order: OrderRow): FullOrderStatus[] {
  const currentStatus = getOrderStatusValue(order);
  if (!currentStatus) return [];
  return (ORDER_STATUS_TRANSITIONS[currentStatus] ?? []).filter(
    (target) =>
      ADMIN_STATUS_ACTION_TARGETS.has(target) &&
      canShowAdminStatusTarget(order, target),
  );
}

function getAdminStatusActionLabel(status: FullOrderStatus): string {
  if (status === "awaiting_inventory") return "Move to Awaiting Inventory";
  if (status === "ready_to_prepare") return "Move to Ready to Prepare";
  if (status === "on_hold") return "Put On Hold";
  return `Move to ${formatStatusLabel(status)}`;
}


function formatPaymentRecoveryLabel(recoveryState: unknown): string {
  return String(recoveryState ?? "").trim().toLowerCase() === "pay_now_recovery"
    ? "Pay Now"
    : "None";
}

function toNumber(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

function formatInventoryRecheckDelta(value: number): string {
  return `${value > 0 ? "+" : ""}${value}`;
}

function getInventoryRecheckBadgeClass(value: number): string {
  if (value === 0) return "border-green-300 bg-green-50 text-green-800";
  if (value > 0) return "border-emerald-300 bg-emerald-50 text-emerald-800";
  if (Math.abs(value) <= 5) return "border-amber-300 bg-amber-50 text-amber-800";
  return "border-red-300 bg-red-50 text-red-800";
}

function OrderItemsExpandedRow({
  row,
  showInventoryRecheckColumn = false,
}: {
  row: Row<OrderRow>;
  showInventoryRecheckColumn?: boolean;
}) {
  const order = row.original;
  const items = order.items;
  const lineItemImpactMap = new Map<string, InventoryRecheckLineItemImpact>(
    (order.inventoryRecheckImpact?.lineItemImpacts ?? []).map((impact) => [impact.cargoId, impact]),
  );
  const gridClass = showInventoryRecheckColumn
    ? "grid grid-cols-[minmax(0,1fr)_minmax(120px,180px)_72px_108px_120px_130px_minmax(150px,1.1fr)] gap-3"
    : "grid grid-cols-[minmax(0,1fr)_minmax(120px,180px)_72px_108px_120px_130px] gap-3";

  if (items.length === 0) {
    return (
      <div className="rounded-md border px-3 py-2 text-sm text-muted-foreground">
        No line items available.
      </div>
    );
  }

  return (
    <div className="w-full space-y-2 px-4 md:px-6">
      <div className={`${gridClass} rounded-md border bg-muted/30 px-3 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground`}>
        <span className="min-w-0">Product</span>
        <span className="truncate">Cargo</span>
        <span className="truncate">Qty</span>
        <span className="truncate">Weight</span>
        <span className="truncate">Unit Price</span>
        <span className="truncate">Line Total</span>
        {showInventoryRecheckColumn ? <span className="truncate">Recheck</span> : null}
      </div>
      <div className="space-y-1">
        {items.map((item, index) => {
          const quantity = toNumber(item.quantity);
          const unitPrice = toNumber(item.unitPrice);
          const unitWeight = toNumber(item.unitWeight);
          const itemWeight = quantity * unitWeight;
          const lineTotal = quantity * unitPrice * unitWeight;
          const cargoImpact = lineItemImpactMap.get(String(item.cargoId ?? "")) ?? null;
          return (
            <div
              key={item._id ?? `${item.productId ?? "product"}-${item.cargoId ?? "cargo"}-${index}`}
              className={`${gridClass} rounded-md px-3 py-2 text-sm`}
            >
              <span className="min-w-0 whitespace-normal break-words">{item.product?.name ?? "-"}</span>
              <span className="min-w-0 truncate">{item.cargo?.ociNumber ?? "-"}</span>
              <span className="min-w-0">{quantity}</span>
              <span className="min-w-0">{itemWeight > 0 ? itemWeight.toFixed(2) : "-"}</span>
              <span className="min-w-0">{formatCurrency(unitPrice)}</span>
              <span className="min-w-0">{formatCurrency(lineTotal)}</span>
              {showInventoryRecheckColumn ? (
                <span className="min-w-0">
                  {cargoImpact ? (
                    <Badge
                      variant="outline"
                      className={getInventoryRecheckBadgeClass(cargoImpact.netAvailableChange)}
                    >
                      {formatInventoryRecheckDelta(cargoImpact.netAvailableChange)} during recheck
                    </Badge>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </span>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function useColumns(
  onAction: (action: string, order: OrderRow) => void,
  showInventoryRecheckColumn: boolean,
  canManageOrders: boolean,
  canApproveFailedPaymentConversion: boolean,
  showSplitPaymentStatusColumn: boolean,
  isStartingEdit: boolean,
) {
  return React.useMemo<ColumnDef<OrderRow>[]>(
    () => [
      {
        id: "orderNumber",
        accessorKey: "orderNumber",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Order #" />
        ),
        cell: ({ row }) => (
          <span className="text-xs font-mono font-medium">
            {row.original.orderNumber}
          </span>
        ),
        meta: {
          label: "Order",
          placeholder: "Search order...",
          variant: "text" as const,
        },
        enableColumnFilter: true,
      },
      {
        id: "isEnterprise",
        accessorKey: "isEnterprise",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Enterprise" />
        ),
        cell: ({ row }) => (
          <Badge
            variant="outline"
            className={getEnterpriseBadgeClass(row.original.isEnterprise)}
          >
            {getEnterpriseBadgeLabel(row.original.isEnterprise)}
          </Badge>
        ),
        meta: {
          label: "Enterprise",
          variant: "select" as const,
          options: [
            { label: "Enterprise", value: "true" },
            { label: "Standard", value: "false" },
          ],
        },
        enableColumnFilter: true,
        filterFn: (row, _columnId, filterValues) => {
          const isEnterprise = row.original.isEnterprise;
          return filterValues.some((val: string) => val === String(isEnterprise));
        },
      },
      {
        id: "customer",
        accessorKey: "customer",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Customer" />
        ),
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="text-xs font-medium">
              {row.original.company || row.original.customer}
            </span>
            {row.original.company && row.original.company !== row.original.customer && (
              <span className="text-xs text-muted-foreground">
                {row.original.customer}
              </span>
            )}
            {isDisplayableSequence(row.original.customerOrderSequence) && (
              <span className={`mt-0.5 inline-flex w-fit items-center rounded border text-[10px] font-medium py-0 px-1.5 animate-badge-shine ${getOrderSequenceBadgeClass(row.original.customerOrderSequence)}`}>
                {getOrderSequenceLabel(row.original.customerOrderSequence)}
              </span>
            )}
          </div>
        ),
        filterFn: (row, _columnId, filterValue) => {
          if (!filterValue) return true;
          const search = String(filterValue).toLowerCase();
          const customer = (row.original.customer ?? "").toLowerCase();
          const email = (row.original.email ?? "").toLowerCase();
          const company = (row.original.company ?? "").toLowerCase();
          return customer.includes(search) || email.includes(search) || company.includes(search);
        },
        meta: {
          label: "Customer",
          placeholder: "Search customer...",
          variant: "text" as const,
        },
        enableColumnFilter: true,
      },
      {
        id: "status",
        accessorKey: "status",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Status" />
        ),
        cell: ({ row }) => (
          <div className="space-y-1">
            <Badge
              variant="outline"
              className={getOrderStatusBadgeClass(row.original.status)}
            >
              {formatStatusLabel(row.original.status)}
            </Badge>
            {row.original.status === "on_hold" && row.original.holdReason ? (
              <p className="max-w-[300px] text-[11px] leading-4 text-red-700 dark:text-red-300">
                {row.original.holdReason}
              </p>
            ) : null}
          </div>
        ),
        meta: {
          label: "Status",
          variant: "select" as const,
          options: [
            { label: "Pending Payment", value: "pending_payment" },
            { label: "Payment Processing", value: "payment_processing" },
            { label: "Awaiting Inventory", value: "awaiting_inventory" },
            { label: "Ready to Prepare", value: "ready_to_prepare" },
            { label: "Ready for Pickup", value: "ready_for_pickup" },
            { label: "In Transit", value: "in_transit" },
            { label: "Delivered", value: "delivered" },
            { label: "On Hold", value: "on_hold" },
            { label: "Cancelled", value: "cancelled" },
          ],
        },
        enableColumnFilter: true,
      },
      ...(showInventoryRecheckColumn
        ? [
            {
              id: "inventoryRecheck",
              accessorFn: (row: OrderRow) => row.inventoryRecheckImpact?.netAvailableChange ?? 0,
              header: ({ column }: any) => (
                <DataTableColumnHeader column={column} label="Recheck" />
              ),
              cell: ({ row }: any) => {
                const impact = row.original.inventoryRecheckImpact;
                if (!impact) {
                  return <span className="text-xs text-muted-foreground">-</span>;
                }

                return (
                  <div className="space-y-1">
                    <Badge
                      variant="outline"
                      className={getInventoryRecheckBadgeClass(impact.netAvailableChange)}
                    >
                      {formatInventoryRecheckDelta(impact.netAvailableChange)}
                    </Badge>
                    <span className="text-[11px] text-muted-foreground">during recheck</span>
                  </div>
                );
              },
              meta: { label: "Recheck", variant: "number" as const },
              enableColumnFilter: false,
            },
          ]
        : []),
      {
        id: "paymentStatus",
        accessorKey: "paymentStatus",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Payment" />
        ),
        cell: ({ row }) => (
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge
              variant="outline"
              className={getPaymentStatusBadgeClass(row.original.paymentStatus)}
            >
              {formatStatusLabel(row.original.paymentStatus)}
            </Badge>
            {row.original.canRecoverPendingPayment ? (
              <Badge
                variant="outline"
                className={getPaymentRecoveryBadgeClass("pay_now_recovery")}
              >
                Pay Now
              </Badge>
            ) : null}
          </div>
        ),
        meta: {
          label: "Payment",
          variant: "select" as const,
          options: [
            { label: "Paid", value: "paid" },
            { label: "Pending", value: "pending" },
            { label: "Partial", value: "partial" },
            { label: "Overdue", value: "overdue" },
            { label: "Failed", value: "failed" },
          ],
        },
        enableColumnFilter: true,
      },
      ...(showSplitPaymentStatusColumn
        ? [
            {
              id: "splitPaymentStatus",
              accessorFn: (row: OrderRow) =>
                row.splitPaymentSummary
                  ? `${row.splitPaymentSummary.completedInstallments}/${row.splitPaymentSummary.totalInstallments}`
                  : "not_split",
              header: ({ column }: any) => (
                <DataTableColumnHeader column={column} label="Split" />
              ),
              cell: ({ row }: any) => {
                const summary = row.original.splitPaymentSummary;
                if (summary === undefined) {
                  return <span className="text-xs text-muted-foreground">—</span>;
                }
                if (!summary) {
                  return (
                    <Badge variant="outline" className={BADGE_TONES.slate}>
                      Not split
                    </Badge>
                  );
                }
                return (
                  <div className="space-y-1">
                    <Badge
                      variant="outline"
                      className={getSplitPaymentBadgeClass(summary)}
                    >
                      {summary.completedInstallments} of {summary.totalInstallments} paid
                    </Badge>
                    {summary.nextDueAt ? (
                      <p className="text-[11px] text-muted-foreground">
                        Next due {new Date(summary.nextDueAt).toLocaleDateString()}
                      </p>
                    ) : null}
                  </div>
                );
              },
              enableColumnFilter: false,
            },
          ]
        : []),
      {
        id: "paymentRecoveryState",
        accessorKey: "paymentRecoveryState",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Recovery" />
        ),
        cell: ({ row }) =>
          row.original.canRecoverPendingPayment ? (
            <Badge
              variant="outline"
              className={getPaymentRecoveryBadgeClass(
                row.original.paymentRecoveryState,
              )}
            >
              {formatPaymentRecoveryLabel(row.original.paymentRecoveryState)}
            </Badge>
          ) : (
            <span className="text-xs text-muted-foreground">-</span>
          ),
        meta: {
          label: "Recovery",
          variant: "select" as const,
          options: [
            { label: "Needs Pay Now", value: "pay_now_recovery" },
            { label: "No Recovery", value: "none" },
          ],
        },
        enableColumnFilter: true,
      },
      {
        id: "quantity",
        accessorKey: "quantity",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Qty" />
        ),
        cell: ({ row }) => (
          <span className="text-xs tabular-nums">{row.original.quantity}</span>
        ),
        meta: { label: "Qty", variant: "number" as const },
        enableColumnFilter: true,
      },
      {
        id: "totalAmount",
        accessorKey: "totalAmount",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Total" />
        ),
        cell: ({ row }) => (
          <span className="text-xs font-medium tabular-nums">
            {formatCurrency(row.original.totalAmount)}
          </span>
        ),
        meta: { label: "Total", variant: "number" as const },
        enableColumnFilter: true,
      },
      {
        id: "paymentMethod",
        accessorFn: (row) => (row.paymentMethod || "").toLowerCase(),
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Method" />
        ),
        cell: ({ row }) => {
          const paymentMethod = row.original.paymentMethod;
          if (!paymentMethod) {
            return <span className="text-xs text-muted-foreground">-</span>;
          }
          return (
            <Badge
              variant="outline"
              className={getPaymentMethodBadgeClass(paymentMethod)}
            >
              {formatPaymentMethodLabel(paymentMethod)}
            </Badge>
          );
        },
        meta: {
          label: "Method",
          variant: "select" as const,
          options: [...paymentMethodFilterOptions],
        },
        enableColumnFilter: true,
      },
      {
        id: "deliveryMethod",
        accessorKey: "deliveryMethod",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Delivery" />
        ),
        cell: ({ row }) => (
          <Badge
            variant="outline"
            className={getDeliveryMethodBadgeClass(row.original.deliveryMethod)}
          >
            {formatDeliveryMethod(row.original.deliveryMethod)}
          </Badge>
        ),
        meta: { label: "Delivery", variant: "text" as const },
        enableColumnFilter: true,
      },
      {
        id: "createdAt",
        accessorKey: "createdAt",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Created" />
        ),
        cell: ({ row }) => {
          if (!row.original.createdAt)
            return (
              <span className="text-xs text-muted-foreground">-</span>
            );
          return (
            <span className="text-xs">
              {new Date(row.original.createdAt).toLocaleDateString()}
            </span>
          );
        },
        meta: { label: "Created", variant: "date" as const },
        enableColumnFilter: true,
      },
      {
        id: "invoices",
        header: "Invoices",
        cell: ({ row }) => {
          const orderId = String(row.original.id ?? "");
          if (!orderId) {
            return <span className="text-xs text-muted-foreground">-</span>;
          }
          return (
            <div className="flex flex-wrap gap-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() =>
                  window.open(`/invoice/${orderId}`, "_blank", "noopener,noreferrer")
                }
              >
                Internal
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() =>
                  window.open(
                    `/invoice/${orderId}/customer`,
                    "_blank",
                    "noopener,noreferrer",
                  )
                }
              >
                Customer
              </Button>
            </div>
          );
        },
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs">
                Actions
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {canManageOrders
                ? getAdminStatusActionTargets(row.original).map((targetStatus) => (
                    <DropdownMenuItem
                      key={targetStatus}
                      onClick={() => onAction(`status:${targetStatus}`, row.original)}
                    >
                      <RefreshCw className="mr-2 h-4 w-4" />
                      {getAdminStatusActionLabel(targetStatus)}
                    </DropdownMenuItem>
                  ))
                : null}
              {canManageOrders ? (
                <DropdownMenuItem
                  disabled={isStartingEdit}
                  onClick={() => onAction("editOrder", row.original)}
                >
                  <Edit3 className="mr-2 h-4 w-4" />
                  {isStartingEdit ? "Putting order on hold..." : "Edit Order"}
                </DropdownMenuItem>
              ) : null}
              {canManageOrders && canStartAdminCancellation(row.original) ? (
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  disabled={isStartingEdit}
                  onClick={() => onAction("cancelOrder", row.original)}
                >
                  <XCircle className="mr-2 h-4 w-4" />
                  Cancel Order
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem
                onClick={() => onAction("markPaid", row.original)}
              >
                <Check className="mr-2 h-4 w-4" />
                Record Manual Payment
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => onAction("reconcileStripe", row.original)}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Reconcile Stripe Payment
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => onAction("paymentLink", row.original)}
              >
                <Link2 className="mr-2 h-4 w-4" />
                Payment Link
              </DropdownMenuItem>
              {canApproveFailedPaymentConversion &&
              hasFailedCardPayment(row.original) ? (
                <DropdownMenuItem
                  onClick={() =>
                    onAction("convertFailedPayment", row.original)
                  }
                >
                  <Split className="mr-2 h-4 w-4" />
                  Convert Failed Payment
                </DropdownMenuItem>
              ) : !hasFailedPayment(row.original) &&
                !row.original.splitPaymentSummary ? (
                <DropdownMenuItem
                  onClick={() => onAction("splitPayment", row.original)}
                >
                  <Split className="mr-2 h-4 w-4" />
                  Split Payment
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem
                onClick={() => onAction("invoiceEmail", row.original)}
              >
                <Mail className="mr-2 h-4 w-4" />
                Send Invoice Email
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
        enableColumnFilter: false,
        enableSorting: false,
      },
    ],
    [
      canApproveFailedPaymentConversion,
      canManageOrders,
      isStartingEdit,
      onAction,
      showInventoryRecheckColumn,
      showSplitPaymentStatusColumn,
    ],
  );
}

export function OrdersTab({
  authUserId,
  orderScope,
  showSplitPaymentStatusColumn = false,
}: OrdersTabProps) {
  const [searchParams] = useSearchParams();
  const { userRole } = useAuthIdentity();
  const canViewInventoryRecheck = hasPermission(userRole, "inventory:view");
  const canManageOrders = hasPermission(userRole, "admin:manage");
  const canApproveFailedPaymentConversion = hasPermission(
    userRole,
    "accounting:approve",
  );

  // Read cargo / contract filters from URL (set by the analytics page dropdowns).
  const productIdQuery = searchParams.get("productId") || undefined;
  const contractSearchQuery = searchParams.get("contractSearch") || undefined;
  const cargoSearchQuery = searchParams.get("cargoSearch") || undefined;

  // ── Phase 1: Light list of ALL orders (1 index range read) ──
  // When contractSearch / cargoSearch is active the backend filters to only
  // orders whose items are linked to a matching cargo.
  const ordersListData = useQuery(
    api.admin.getOrdersList,
    authUserId
        ? {
            authUserId,
            orderScope,
            productId: productIdQuery,
            contractSearch: contractSearchQuery,
            cargoSearch: cargoSearchQuery,
          }
      : "skip",
  );

  // Track which order IDs to enrich (current visible page).
  // Updated by the table when page/sort/filter changes.
  const [visibleOrderIds, setVisibleOrderIds] = React.useState<string[]>([]);

  // ── Phase 2: Enrich only the visible page ──
  const enrichedData = useQuery(
    api.admin.getOrdersEnriched,
    authUserId && visibleOrderIds.length > 0
      ? { authUserId, orderIds: visibleOrderIds as any }
      : "skip",
  ) as Record<string, any> | undefined;

  const activeInventoryRecheckSummary = useQuery(
    api.inventoryRecheck.getActiveSessionSummary,
    authUserId && canViewInventoryRecheck ? {} : "skip",
  ) as InventoryRecheckSessionSummary | null | undefined;
  const orderIdsForRecheck = React.useMemo(
    () =>
      Array.isArray(ordersListData)
        ? ordersListData
            .map((order: any) => String(order?._id ?? "").trim())
            .filter((orderId: string): orderId is string => orderId.length > 0)
        : [],
    [ordersListData],
  );
  const inventoryRecheckOrderData = useQuery(
    api.inventoryRecheck.getOrderImpacts,
    authUserId && canViewInventoryRecheck && orderIdsForRecheck.length > 0
      ? { orderIds: orderIdsForRecheck as any }
      : "skip",
  ) as InventoryRecheckOrderImpactResponse | undefined;

  // ── Mutations & Actions ──
  const recordPaymentMut = useMutation(api.payments.recordManualPayment);
  const updateOrderStatusMut = useMutation(api.logistics.updateOrderStatus);
  const beginOrderWorkflow = useMutation(api.adminOrderWorkflow.beginOrderWorkflow);
  const adminCancellation = useAdminOrderCancellation(authUserId);
  const createPaymentLinkAction = useAction(
    api.stripePayments.createPaymentLink,
  );
  const sendInvoiceEmailAction = useAction(api.orders.sendInvoiceEmailAction);
  const reconcileStripePaymentAction = useAction(
    api.payments.reconcileStripePaymentForOrder,
  );
  const splitPaymentMut = useMutation(api.payments.splitOrderPayment);

  // ── Dialog State ──
  const [selectedOrder, setSelectedOrder] = React.useState<OrderRow | null>(
    null,
  );

  // Mark as Paid dialog
  const [showMarkPaid, setShowMarkPaid] = React.useState(false);
  const [paymentMethod, setPaymentMethod] = React.useState("cheque");
  const [paymentAmount, setPaymentAmount] = React.useState("");
  const [paymentReference, setPaymentReference] = React.useState("");
  const [paymentNotes, setPaymentNotes] = React.useState("");
  const [isSubmittingPayment, setIsSubmittingPayment] = React.useState(false);

  // Stripe reconciliation dialog
  const [showStripeReconcile, setShowStripeReconcile] = React.useState(false);
  const [stripePaymentIntentId, setStripePaymentIntentId] = React.useState("");
  const [stripeScheduledPaymentId, setStripeScheduledPaymentId] = React.useState("");
  const [allowStripeAmountAdjustment, setAllowStripeAmountAdjustment] =
    React.useState(false);
  const [stripeAdjustmentReason, setStripeAdjustmentReason] = React.useState("");
  const [stripeReconcilePreview, setStripeReconcilePreview] = React.useState<any>(
    null,
  );
  const [stripeReconcileMessage, setStripeReconcileMessage] = React.useState("");
  const [isReconcilingStripe, setIsReconcilingStripe] = React.useState(false);

  // Payment Link dialog
  const [showPaymentLink, setShowPaymentLink] = React.useState(false);
  const [paymentLinkMethod, setPaymentLinkMethod] =
    React.useState<PaymentLinkMethod>("us_bank_account");
  const [generatedPaymentLink, setGeneratedPaymentLink] = React.useState<
    string | null
  >(null);
  const [isGeneratingLink, setIsGeneratingLink] = React.useState(false);

  // Split Payment dialog
  const [showSplitPayment, setShowSplitPayment] = React.useState(false);
  const [showFailedPaymentConversion, setShowFailedPaymentConversion] =
    React.useState(false);
  const [showAdminAmendmentModal, setShowAdminAmendmentModal] = React.useState(false);
  const [selectedAmendmentOrder, setSelectedAmendmentOrder] = React.useState<OrderRow | null>(null);
  const [isStartingEdit, setIsStartingEdit] = React.useState(false);
  const startingEditRef = React.useRef(false);
  const [isSplittingPayment, setIsSplittingPayment] = React.useState(false);
  const [splitPaymentResult, setSplitPaymentResult] = React.useState<any>(
    null,
  );
  const [selectedSplitPaymentMethodId, setSelectedSplitPaymentMethodId] =
    React.useState("");
  const [customInstallments, setCustomInstallments] = React.useState<
    Array<{ installmentNumber: number; amount: number; daysAfterOrder: number; dueDate: string }>
  >([]);
  const [isEditingInstallments, setIsEditingInstallments] = React.useState(false);
  const [isSendingInvoice, setIsSendingInvoice] = React.useState(false);

  const splitPaymentCustomerId =
    showSplitPayment && typeof selectedOrder?.rawOrder?.userId === "string"
      ? selectedOrder.rawOrder.userId
      : undefined;
  const splitPaymentMethods = useQuery(
    api.paymentMethods.getPaymentMethodsForCustomer,
    authUserId && splitPaymentCustomerId
      ? {
          authUserId,
          userId: splitPaymentCustomerId as any,
        }
      : "skip",
  ) as SplitPaymentMethodOption[] | undefined;
  const linkedCreditPaymentMethod = useQuery(
    api.paymentMethods.getLinkedCreditPaymentMethodForCustomer,
    authUserId && splitPaymentCustomerId
      ? {
          authUserId,
          userId: splitPaymentCustomerId as any,
        }
      : "skip",
  ) as LinkedCreditPaymentMethodSnapshot | null | undefined;

  const isLoading = ordersListData === undefined;

  // Build table rows from light list, merging in enrichment for visible rows
  const tableData = React.useMemo((): OrderRow[] => {
    if (!ordersListData || !Array.isArray(ordersListData)) return [];
    const impactMap = new Map(
      (inventoryRecheckOrderData?.impacts ?? []).map((impact) => [impact.orderId, impact]),
    );
    return (ordersListData as any[]).map((order: any, index: number) => {
      const orderId = String(order._id || `order-${index}`);
      const enrichment = enrichedData?.[orderId];
      const timestamp = order.createdAt || order._creationTime || 0;
      const lightCustomer =
        (typeof order.customerName === "string" && order.customerName.trim())
        || (typeof order.customerEmail === "string" && order.customerEmail.trim())
        || "\u2014";
      const lightEmail = typeof order.customerEmail === "string" ? order.customerEmail : "";
      const lightCompany = typeof order.company === "string" ? order.company : "";

      // Enriched fields (available only when this row's page has been fetched)
      const customerName = enrichment
        ? (enrichment.user?.name || enrichment.user?.email || lightCustomer)
        : lightCustomer;
      const email = enrichment
        ? (enrichment.user?.email || lightEmail)
        : lightEmail;
      const company = enrichment
        ? (enrichment.user?.company || lightCompany)
        : lightCompany;
      const deliveryMethod = enrichment
        ? formatDeliveryMethod(enrichment.delivery?.deliveryMethod, "N/A")
        : "\u2014";
      const items: OrderItemRow[] = enrichment
        ? (Array.isArray(enrichment.items) ? enrichment.items : [])
        : [];
      const itemCount = items.length;

      // totalAmount: prefer enriched (recalculated) value, fall back to order-level locked total
      const rawTotal = enrichment?.totalAmount ?? order.totalAmount;
      const numericTotal =
        typeof rawTotal === "number"
          ? rawTotal
          : typeof rawTotal === "string"
            ? parseFloat(rawTotal.replace(/[^0-9.-]/g, "")) || 0
            : 0;

      // Payment fields from enrichment
      const paymentMethodValue: string = enrichment
        ? (() => {
            const method = enrichment.payment?.method || "";
            const type = enrichment.payment?.type || "";
            const linked = enrichment.payment?.linkedPaymentMethodType || "";
            if (method === "credit" || type === "credit_limit") {
              const lt = (linked || type).toLowerCase();
              if (lt === "us_bank_account" || lt === "ach" || lt === "bank") return "credit (bank)";
              if (lt === "card") return "credit (card)";
              return "credit";
            }
            if (type === "us_bank_account" || method === "ach") return "us_bank_account";
            return method;
          })()
        : "\u2014";
      const canRecoverPendingPayment = Boolean(
        enrichment?.payment?.canRecoverPendingPayment ??
          order.canRecoverPendingPayment,
      );

      const operationalStatus =
        typeof enrichment?.operationalStatus === "string" && enrichment.operationalStatus.trim()
          ? enrichment.operationalStatus
          : order.status || "unknown";
      const holdReason =
        typeof enrichment?.holdReason === "string" && enrichment.holdReason.trim()
          ? enrichment.holdReason
          : null;

      return {
        id: orderId,
        orderNumber: order.orderNumber || "N/A",
        customer: customerName,
        email,
        company,
        status: operationalStatus,
        holdReason,
        paymentStatus: enrichment
          ? (enrichment.payment?.status || "pending")
          : "\u2014",
        splitPaymentSummary: enrichment
          ? (enrichment.splitPaymentSummary ?? null)
          : undefined,
        paymentRecoveryState: canRecoverPendingPayment
          ? "pay_now_recovery"
          : "none",
        canRecoverPendingPayment,
        totalAmount: numericTotal,
        quantity: itemCount,
        deliveryMethod,
        paymentMethod: paymentMethodValue,
        isEnterprise: order.isEnterprise || false,
        createdAt: timestamp
          ? new Date(timestamp).toISOString()
          : "",
        paymentLinkUrl: order.paymentLinkUrl || "",
        items,
        customerOrderSequence: enrichment?.customerOrderSequence ?? null,
        inventoryRecheckImpact: impactMap.get(orderId.trim()) ?? null,
        rawOrder: { ...order, ...(enrichment || {}) },
      };
    });
  }, [inventoryRecheckOrderData?.impacts, ordersListData, enrichedData]);

  const orderNumberParam = searchParams.get("orderNumber");
  const clientIdParam = searchParams.get("clientId");
  const companyParam = searchParams.get("company");
  const statusParam = searchParams.get("status");
  const productIdParam = searchParams.get("productId");
  const productNameParam = searchParams.get("productName");
  const fromParam = searchParams.get("from");
  const toParam = searchParams.get("to");
  // contractSearch / cargoSearch are handled server-side by getOrdersList.

  const scopedTableData = React.useMemo((): OrderRow[] => {
    const fromMs = parseDateQueryParam(fromParam);
    const toMs = parseDateQueryParam(toParam, true);

    return tableData.filter((row) => {
      const normalizedStatus = String(row.status).trim().toLowerCase();
      const hasExplicitStatusFilter = Boolean(statusParam);

      if (
        orderScope === "orders"
        && (normalizedStatus === "cancelled"
          || isHiddenInInternalOrderTablesByDefault(normalizedStatus))
      ) {
        return false;
      }
      if (orderScope === "abandoned" && !ABANDONED_ORDER_STATUSES.has(normalizedStatus)) {
        return false;
      }
      if (orderScope === "both" && normalizedStatus === "cancelled") {
        return false;
      }
      if (
        !orderScope
        && !hasExplicitStatusFilter
        && isHiddenInInternalOrderTablesByDefault(normalizedStatus)
      ) {
        return false;
      }

      if (!matchesScopedFilter([row.orderNumber], orderNumberParam)) return false;
      // When a clientId (userProfiles._id) is in the URL, filter by the order's
      // userId which is available in light data — no enrichment needed.
      // Fall back to light customer identity fields when there's no clientId.
      if (clientIdParam) {
        if (row.rawOrder?.userId !== clientIdParam) return false;
      } else if (!matchesScopedFilter([row.company, row.customer, row.email], companyParam)) {
        return false;
      }
      if (statusParam && normalizedStatus !== String(statusParam).trim().toLowerCase()) {
        return false;
      }
      if (
        !productIdParam
        &&
        !matchesScopedFilter(
          row.items.flatMap((item) => [
            item.product?.name,
            (item as { options?: { productName?: string } | null }).options?.productName,
          ]),
          productNameParam,
        )
      ) {
        return false;
      }

      // contractSearch / cargoSearch filtering is handled server-side
      // by getOrdersList — no client-side filtering needed here.

      const createdAtMs = Date.parse(row.createdAt);
      if (fromMs !== null && (!Number.isFinite(createdAtMs) || createdAtMs < fromMs)) {
        return false;
      }
      if (toMs !== null && (!Number.isFinite(createdAtMs) || createdAtMs > toMs)) {
        return false;
      }

      return true;
    });
  }, [
    tableData,
    orderNumberParam,
    clientIdParam,
    companyParam,
    statusParam,
    productIdParam,
    productNameParam,
    fromParam,
    toParam,
    orderScope,
  ]);

  // ── Action Handler ──
  const onAction = React.useCallback(
    (action: string, order: OrderRow) => {
      setSelectedOrder(order);
      if (action.startsWith("status:")) {
        if (!authUserId) {
          toast.error("Missing auth context for status update");
          return;
        }

        const newStatus = action.slice("status:".length) as FullOrderStatus;
        void updateOrderStatusMut({
          authUserId,
          orderId: order.id as any,
          newStatus,
          notes: "Manual status change from admin orders page",
        })
          .then(() => {
            toast.success(`Order moved to ${formatStatusLabel(newStatus)}`);
          })
          .catch((error: any) => {
            toast.error(
              error instanceof Error
                ? error.message
                : "Failed to update order status",
            );
          });
        return;
      }

      if (action === "markPaid") {
        setPaymentAmount(order.totalAmount.toFixed(2));
        setPaymentMethod("cheque");
        setPaymentReference("");
        setPaymentNotes("");
        setShowMarkPaid(true);
      } else if (action === "cancelOrder") {
        void adminCancellation.start(order);
      } else if (action === "reconcileStripe") {
        setStripePaymentIntentId("");
        setStripeScheduledPaymentId("");
        setAllowStripeAmountAdjustment(false);
        setStripeAdjustmentReason("");
        setStripeReconcilePreview(null);
        setStripeReconcileMessage("");
        setShowStripeReconcile(true);
      } else if (action === "editOrder") {
        if (!authUserId) {
          toast.error("Missing auth context for admin editing");
          return;
        }

        if (startingEditRef.current) return;
        startingEditRef.current = true;
        setIsStartingEdit(true);
        void beginOrderWorkflow({
          authUserId,
          orderId: order.id as any,
          reason: "edit",
        })
          .then(() => {
            setSelectedAmendmentOrder(order);
            setShowAdminAmendmentModal(true);
          })
          .catch((error: any) => {
            toast.error(
              toUserFacingErrorMessage(error, "Unable to put the order on hold for editing. Please try again."),
            );
          })
          .finally(() => {
            startingEditRef.current = false;
            setIsStartingEdit(false);
          });
      } else if (action === "paymentLink") {
        setPaymentLinkMethod("us_bank_account");
        setGeneratedPaymentLink(null);
        setShowPaymentLink(true);
      } else if (action === "splitPayment") {
        setSplitPaymentResult(null);
        setShowSplitPayment(true);
      } else if (action === "convertFailedPayment") {
        setShowFailedPaymentConversion(true);
      } else if (action === "invoiceEmail") {
        const targetOrderId = order.rawOrder?._id;
        if (!targetOrderId) {
          toast.error("Unable to resolve order ID for invoice email");
          return;
        }

        setIsSendingInvoice(true);
        void sendInvoiceEmailAction({ orderId: targetOrderId as any })
          .then((result: any) => {
            if (result?.success) {
              toast.success(
                result?.sentTo
                  ? `Invoice email sent to ${result.sentTo}`
                  : "Invoice email sent",
              );
            } else {
              toast.error(result?.error || "Failed to send invoice email");
            }
          })
          .catch((error: any) => {
            toast.error(
              error instanceof Error
                ? error.message
                : "Failed to send invoice email",
            );
          })
          .finally(() => {
            setIsSendingInvoice(false);
          });
      }
    },
    [authUserId, adminCancellation.start, beginOrderWorkflow, sendInvoiceEmailAction, updateOrderStatusMut],
  );

  const columns = useColumns(
    onAction,
    Boolean(activeInventoryRecheckSummary),
    canManageOrders,
    canApproveFailedPaymentConversion,
    showSplitPaymentStatusColumn,
    isStartingEdit || adminCancellation.isStarting,
  );

  // ── Mark as Paid handler ──
  async function handleMarkAsPaid() {
    if (!selectedOrder || !authUserId || !paymentAmount) return;
    setIsSubmittingPayment(true);
    try {
      await recordPaymentMut({
        authUserId,
        orderId: selectedOrder.id as any,
        amount: parseFloat(paymentAmount),
        method: paymentMethod as any,
        reference: paymentReference || undefined,
        notes: paymentNotes || undefined,
      });
      setShowMarkPaid(false);
      setSelectedOrder(null);
    } catch (err: any) {
      console.error("Failed to record payment:", err);
    } finally {
      setIsSubmittingPayment(false);
    }
  }

  // ── Stripe reconciliation handler ──
  async function handleReconcileStripePayment(dryRun: boolean) {
    if (!selectedOrder || !stripePaymentIntentId.trim()) return;

    setIsReconcilingStripe(true);
    try {
      const result = await reconcileStripePaymentAction({
        orderId: selectedOrder.id as any,
        stripePaymentIntentId: stripePaymentIntentId.trim(),
        scheduledPaymentId: stripeScheduledPaymentId.trim()
          ? (stripeScheduledPaymentId.trim() as any)
          : undefined,
        allowAmountAdjustment: allowStripeAmountAdjustment,
        adjustmentReason: allowStripeAmountAdjustment
          ? stripeAdjustmentReason.trim()
          : undefined,
        dryRun,
      });

      setStripeReconcilePreview((result as any)?.data ?? null);
      setStripeReconcileMessage((result as any)?.message ?? "");

      if ((result as any)?.success) {
        if ((result as any)?.data?.requiresAmountAdjustment) {
          toast.error("Amount mismatch needs confirmation");
          return;
        }

        if (dryRun) {
          toast.success("Stripe reconciliation preview ready");
          return;
        }

        toast.success("Stripe payment reconciled");
        setShowStripeReconcile(false);
        setSelectedOrder(null);
      } else {
        toast.error((result as any)?.error || "Failed to reconcile Stripe payment");
      }
    } catch (err: any) {
      console.error("Failed to reconcile Stripe payment:", err);
      toast.error(
        err instanceof Error ? err.message : "Failed to reconcile Stripe payment",
      );
    } finally {
      setIsReconcilingStripe(false);
    }
  }

  // ── Payment Link handler ──
  async function handleGeneratePaymentLink() {
    if (!selectedOrder) return;
    setIsGeneratingLink(true);
    try {
      const result = await createPaymentLinkAction({
        orderId: selectedOrder.id as any,
        paymentMethod: paymentLinkMethod,
      });
      if ((result as any)?.success && (result as any)?.paymentLinkUrl) {
        setGeneratedPaymentLink((result as any).paymentLinkUrl);
        toast.success("Payment link generated");
        return;
      }

      toast.error((result as any)?.error || "Failed to generate payment link");
    } catch (err: any) {
      console.error("Failed to generate payment link:", err);
      toast.error(
        err instanceof Error ? err.message : "Failed to generate payment link",
      );
    } finally {
      setIsGeneratingLink(false);
    }
  }

  function copyPaymentLink() {
    if (generatedPaymentLink) {
      navigator.clipboard.writeText(generatedPaymentLink);
    }
  }

  // ── Split Payment handler ──
  const splitPaymentOrderTotal = selectedOrder?.totalAmount ?? 0;
  const orderDateForInstallments = React.useMemo(() => {
    if (!selectedOrder?.createdAt) return null;
    const parsed = new Date(selectedOrder.createdAt);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }, [selectedOrder?.createdAt]);

  const splitPaymentPreview = React.useMemo(() => {
    if (!selectedOrder || !orderDateForInstallments) return [];
    const total = selectedOrder.totalAmount;
    const installmentAmount = Math.round((total / 3) * 100) / 100;
    const remainder = Math.round((total - installmentAmount * 2) * 100) / 100;
    const orderDate = orderDateForInstallments;
    return [
      {
        installmentNumber: 1,
        amount: installmentAmount,
        days: 30,
        dueDate: new Date(
          orderDate.getTime() + 30 * 24 * 60 * 60 * 1000,
        ).toISOString(),
      },
      {
        installmentNumber: 2,
        amount: installmentAmount,
        days: 45,
        dueDate: new Date(
          orderDate.getTime() + 45 * 24 * 60 * 60 * 1000,
        ).toISOString(),
      },
      {
        installmentNumber: 3,
        amount: remainder,
        days: 60,
        dueDate: new Date(
          orderDate.getTime() + 60 * 24 * 60 * 60 * 1000,
        ).toISOString(),
      },
    ];
  }, [selectedOrder, orderDateForInstallments]);

  const selectedOrderLinkedPaymentMethodType = React.useMemo(() => {
    const linkedType =
      selectedOrder?.rawOrder?.payment?.linkedPaymentMethodType ||
      selectedOrder?.rawOrder?.linkedPaymentMethodType ||
      "";
    const normalized = String(linkedType).trim().toLowerCase();
    if (normalized === "us_bank_account" || normalized === "ach") return "ach";
    if (normalized === "card") return "card";

    const rowMethod = String(selectedOrder?.paymentMethod ?? "").trim().toLowerCase();
    if (rowMethod.includes("bank") || rowMethod === "us_bank_account") return "ach";
    if (rowMethod.includes("card")) return "card";

    return undefined;
  }, [selectedOrder]);

  const selectedSplitPaymentMethod = React.useMemo(
    () =>
      splitPaymentMethods?.find(
        (paymentMethod) => paymentMethod._id === selectedSplitPaymentMethodId,
      ) ?? null,
    [selectedSplitPaymentMethodId, splitPaymentMethods],
  );

  // Initialize custom installments when dialog opens
  React.useEffect(() => {
    if (showSplitPayment && splitPaymentPreview.length > 0 && !splitPaymentResult) {
      setCustomInstallments(buildCustomInstallmentDrafts(splitPaymentPreview));
      setIsEditingInstallments(false);
    }
  }, [showSplitPayment, splitPaymentPreview, splitPaymentResult]);

  React.useEffect(() => {
    if (!showSplitPayment) {
      setSelectedSplitPaymentMethodId("");
      return;
    }

    if (!splitPaymentMethods || splitPaymentMethods.length === 0) return;

    setSelectedSplitPaymentMethodId((current) => {
      if (
        current &&
        splitPaymentMethods.some((paymentMethod) => paymentMethod._id === current)
      ) {
        return current;
      }

      const preferredMethod =
        splitPaymentMethods.find(
          (paymentMethod) =>
            paymentMethod.type === selectedOrderLinkedPaymentMethodType,
        ) ??
        splitPaymentMethods.find((paymentMethod) => paymentMethod.isDefault) ??
        splitPaymentMethods[0];

      return preferredMethod?._id ?? "";
    });
  }, [showSplitPayment, splitPaymentMethods, selectedOrderLinkedPaymentMethodType]);

  const splitPaymentAchSelected = selectedSplitPaymentMethod?.type === "ach";

  const formatSplitPaymentMethodLabel = React.useCallback(
    (paymentMethod: SplitPaymentMethodOption) => {
      if (paymentMethod.displayName) return paymentMethod.displayName;
      if (paymentMethod.type === "ach") {
        if (paymentMethod.bankName && paymentMethod.last4) {
          return `ACH ${paymentMethod.bankName} ••••${paymentMethod.last4}`;
        }
        if (paymentMethod.last4) return `ACH ••••${paymentMethod.last4}`;
        return "ACH";
      }

      if (paymentMethod.brand && paymentMethod.last4) {
        return `${paymentMethod.brand} ••••${paymentMethod.last4}`;
      }
      if (paymentMethod.last4) return `Card ••••${paymentMethod.last4}`;
      return "Card";
    },
    [],
  );

  async function handleSplitPayment() {
	if (!selectedOrder) return;
	if (splitPaymentMethods?.length && !selectedSplitPaymentMethodId) {
		toast.error("Select the saved ACH or card method for these installments.");
		return;
	}
	setIsSplittingPayment(true);
	try {
		// Build installments payload - use custom if editing, otherwise let backend use defaults
		const installmentsPayload = isEditingInstallments && customInstallments.length === 3
        ? customInstallments.map(inst => ({
            installmentNumber: inst.installmentNumber,
            amount: inst.amount,
            daysAfterOrder: inst.daysAfterOrder,
          }))
        : undefined;

		const result = await splitPaymentMut({
			orderId: selectedOrder.id as any,
			paymentMethodId: selectedSplitPaymentMethodId || undefined,
			installments: installmentsPayload,
		});

		if (!result?.success) {
			toast.error(result?.message || "Failed to split payment");
			return;
		}

		setSplitPaymentResult(result.data ?? null);
    } catch (err: any) {
      console.error("Failed to split payment:", err);
		toast.error(err instanceof Error ? err.message : "Failed to split payment");
    } finally {
      setIsSplittingPayment(false);
    }
  }

  // Handle date change for a specific installment
  const handleInstallmentDateChange = (installmentNumber: number, newDateStr: string) => {
    if (!selectedOrder?.createdAt || !newDateStr) return;
    
    const orderDate = new Date(selectedOrder.createdAt);
    if (Number.isNaN(orderDate.getTime())) return;
    const [year, month, day] = newDateStr.split("-").map(Number);
    if (!year || !month || !day) return;
    
    // Calculate days after order
    const orderDateUtc = Date.UTC(
      orderDate.getUTCFullYear(),
      orderDate.getUTCMonth(),
      orderDate.getUTCDate(),
    );
    const newDueDateUtc = Date.UTC(year, month - 1, day);
    const diffTime = newDueDateUtc - orderDateUtc;
    const daysAfterOrder = Math.max(0, Math.round(diffTime / (1000 * 60 * 60 * 24)));
    
    setCustomInstallments(prev =>
      prev.map(inst =>
        inst.installmentNumber === installmentNumber
          ? { ...inst, daysAfterOrder, dueDate: newDateStr }
          : inst
      )
    );
  };

  // ── Payment Link pricing helpers ──
  const selectedOrderPaymentMethodDiscount =
    typeof selectedOrder?.rawOrder?.paymentMethodDiscount === "number"
      ? selectedOrder.rawOrder.paymentMethodDiscount
      : typeof selectedOrder?.rawOrder?.paymentMethodDiscount === "string"
        ? parseFloat(selectedOrder.rawOrder.paymentMethodDiscount) || 0
        : 0;
  const selectedOrderArChargesTotal =
    typeof selectedOrder?.rawOrder?.arChargesTotal === "number"
      ? selectedOrder.rawOrder.arChargesTotal
      : typeof selectedOrder?.rawOrder?.arChargesTotal === "string"
        ? parseFloat(selectedOrder.rawOrder.arChargesTotal) || 0
        : 0;
  const paymentLinkAmountBeforeDiscount =
    (selectedOrder?.totalAmount ?? 0) + selectedOrderPaymentMethodDiscount - selectedOrderArChargesTotal;
  const paymentLinkAchDiscountAmount =
    paymentLinkMethod === "us_bank_account"
      ? Math.round(paymentLinkAmountBeforeDiscount * 0.03 * 100) / 100
      : 0;
  const paymentLinkAchTotal =
    paymentLinkAmountBeforeDiscount - paymentLinkAchDiscountAmount + selectedOrderArChargesTotal;

  // ── Export ──
  const exportColumns = React.useMemo<ExportColumn[]>(
    () => [
      { key: "orderNumber", label: "Order #" },
      { key: "isEnterprise", label: "Enterprise", format: (v: any) => v ? "Enterprise" : "Standard" },
      { key: "customer", label: "Customer" },
      { key: "company", label: "Company" },
      { key: "status", label: "Status" },
      { key: "quantity", label: "Qty" },
      {
        key: "totalAmount",
        label: "Total",
        format: formatCurrencyForExport,
      },
      { key: "paymentStatus", label: "Payment" },
      {
        key: "paymentRecoveryState",
        label: "Recovery",
        format: (value: unknown) => formatPaymentRecoveryLabel(value),
      },
      { key: "paymentMethod", label: "Method" },
      { key: "deliveryMethod", label: "Delivery" },
      { key: "createdAt", label: "Created", format: formatDateForExport },
    ],
    [],
  );

  const exportRows = React.useCallback(
    (rows: OrderRow[], filename: string) => {
      if (rows.length === 0) {
        toast.error("No orders selected");
        return;
      }
      exportToCSV(rows, exportColumns, filename);
    },
    [exportColumns],
  );

  const handleExport = React.useCallback(() => {
    exportRows(
      scopedTableData,
      `orders-${new Date().toISOString().split("T")[0]}.csv`,
    );
  }, [exportRows, scopedTableData]);

  const handleExportSelected = React.useCallback(
    (selectedRows: OrderRow[]) => {
      exportRows(
        selectedRows,
        `orders-selected-${new Date().toISOString().split("T")[0]}.csv`,
      );
    },
    [exportRows],
  );

  return (
    <>
      <CreditKeyCaptureQueue />
      <Card>
        <CardContent className="pt-6">
          <div className="mb-4">
            <h3 className="text-lg font-semibold leading-none tracking-tight">Orders</h3>
            <p className="text-sm text-muted-foreground">
              All orders with payment management and status tracking.
            </p>
            {isSendingInvoice ? (
              <p className="mt-1 text-xs text-muted-foreground">
                Sending invoice email...
              </p>
            ) : null}
          </div>
          {activeInventoryRecheckSummary ? (
            <div className="mb-4 rounded-md border border-amber-200 bg-amber-50/80 p-3">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className="border-amber-300 bg-white text-amber-900">
                      Inventory recheck active
                    </Badge>
                    <Badge
                      variant="outline"
                      className={getInventoryRecheckBadgeClass(activeInventoryRecheckSummary.currentDeltaBags)}
                    >
                      Live delta {formatInventoryRecheckDelta(activeInventoryRecheckSummary.currentDeltaBags)}
                    </Badge>
                  </div>
                  <p className="text-sm text-amber-950">
                    Tracking order-driven availability movement since {new Date(activeInventoryRecheckSummary.startedAt).toLocaleString()}
                    {activeInventoryRecheckSummary.warehouseName ? ` for ${activeInventoryRecheckSummary.warehouseName}` : ""}.
                  </p>
                </div>
                <div className="grid gap-2 text-sm text-amber-950 sm:grid-cols-3">
                  <div className="rounded-md border border-amber-200 bg-white px-3 py-2">
                    <p className="text-[11px] uppercase tracking-wide text-amber-700">Orders touched</p>
                    <p className="font-semibold">{activeInventoryRecheckSummary.impactedOrderCount}</p>
                  </div>
                  <div className="rounded-md border border-amber-200 bg-white px-3 py-2">
                    <p className="text-[11px] uppercase tracking-wide text-amber-700">Order delta</p>
                    <p className="font-semibold">{formatInventoryRecheckDelta(activeInventoryRecheckSummary.orderDeltaBags)}</p>
                  </div>
                  <div className="rounded-md border border-amber-200 bg-white px-3 py-2">
                    <p className="text-[11px] uppercase tracking-wide text-amber-700">Auto-close</p>
                    <p className="font-semibold">{new Date(activeInventoryRecheckSummary.expiresAt).toLocaleString()}</p>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
          {isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-96 w-full" />
            </div>
          ) : scopedTableData.length > 0 ? (
            <>
              <div className="mb-2 flex items-center justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExport}
                >
                  <Download className="mr-2 h-4 w-4" />
                  Export All
                </Button>
              </div>
              <InternalEntityDataTable
                data={scopedTableData}
                columns={columns}
                pageSize={20}
                initialSorting={[{ id: "createdAt", desc: true }]}
                onVisibleRowsChange={setVisibleOrderIds}
                actionBar={({ selectedRows, clearSelection }) => (
                  <DataTableFloatingBar
                    selectedCount={selectedRows.length}
                    onClear={clearSelection}
                  >
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleExportSelected(selectedRows)}
                    >
                      <Download className="mr-1.5 h-3.5 w-3.5" />
                      Export Selected
                    </Button>
                  </DataTableFloatingBar>
                )}
                expandedRow={{
                  render: ({ row }) => (
                    <OrderItemsExpandedRow
                      row={row}
                      showInventoryRecheckColumn={Boolean(activeInventoryRecheckSummary)}
                    />
                  ),
                  getCanExpand: (order) => order.items.length > 0,
                }}
              />
            </>
          ) : (
            <div className="py-8 text-center text-muted-foreground">
              No orders found
            </div>
          )}
        </CardContent>
      </Card>

      {showAdminAmendmentModal && selectedAmendmentOrder && authUserId ? (
        <AdminAmendmentModal
          open={showAdminAmendmentModal}
          onClose={() => {
            setShowAdminAmendmentModal(false);
            setSelectedAmendmentOrder(null);
          }}
          onSuccess={() => {
            setShowAdminAmendmentModal(false);
            setSelectedAmendmentOrder(null);
          }}
          orderId={selectedAmendmentOrder.id}
          orderItems={selectedAmendmentOrder.items.map((item) => ({
            _id: item._id ?? `item-${item.productId ?? "unknown"}-${item.cargoId ?? "unknown"}`,
            productId: item.productId,
            cargoId: item.cargoId,
            product: item.product,
            cargo: item.cargo,
            quantity: item.quantity ?? 0,
            unitPrice: item.unitPrice ?? 0,
            unitWeight: item.unitWeight ?? 0,
          }))}
          authUserId={authUserId}
          orderNumber={selectedAmendmentOrder.orderNumber}
        />
      ) : null}

      <AdminCancellationDialog
        order={adminCancellation.order}
        authUserId={authUserId}
        onClose={adminCancellation.close}
      />

      {/* ── Mark as Paid Dialog ── */}
      <Dialog open={showMarkPaid} onOpenChange={setShowMarkPaid}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription>
              Record a manual payment for order{" "}
              {selectedOrder?.orderNumber}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="payment-method">Payment Method</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="wire">Wire Transfer</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="payment-amount">Amount ($)</Label>
              <Input
                id="payment-amount"
                type="number"
                step="0.01"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="payment-reference">
                {paymentMethod === "cheque"
                  ? "Cheque Number"
                  : paymentMethod === "wire"
                    ? "Wire Reference"
                    : "Reference / Collected By"}
              </Label>
              <Input
                id="payment-reference"
                placeholder={
                  paymentMethod === "cheque"
                    ? "e.g., 12345"
                    : paymentMethod === "wire"
                      ? "e.g., Wire transfer ID"
                      : "e.g., John Doe"
                }
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="payment-notes">Notes (Optional)</Label>
              <Textarea
                id="payment-notes"
                placeholder="Additional notes about this payment..."
                value={paymentNotes}
                onChange={(e) => setPaymentNotes(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowMarkPaid(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleMarkAsPaid}
              disabled={isSubmittingPayment || !paymentAmount}
            >
              {isSubmittingPayment ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Recording...
                </>
              ) : (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  Record Payment
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Stripe Reconciliation Dialog ── */}
      <Dialog open={showStripeReconcile} onOpenChange={setShowStripeReconcile}>
        <DialogContent className="sm:max-w-[640px]">
          <DialogHeader>
            <DialogTitle>Reconcile Stripe Payment</DialogTitle>
            <DialogDescription>
              Use this when Stripe succeeded but Anthem did not mark order{" "}
              {selectedOrder?.orderNumber} paid.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800/60 dark:bg-amber-950/30 dark:text-amber-200">
              Confirm the PaymentIntent in Stripe first. This keeps the payment linked
              to Stripe and should not be used for cheque, wire, or cash payments.
            </div>
            <div className="grid gap-2">
              <Label htmlFor="stripe-payment-intent-id">Stripe PaymentIntent ID</Label>
              <Input
                id="stripe-payment-intent-id"
                placeholder="pi_..."
                value={stripePaymentIntentId}
                onChange={(event) => {
                  setStripePaymentIntentId(event.target.value);
                  setStripeReconcilePreview(null);
                  setStripeReconcileMessage("");
                }}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="stripe-scheduled-payment-id">
                Scheduled payment ID (required for multi-installment orders)
              </Label>
              <Input
                id="stripe-scheduled-payment-id"
                placeholder="Optional unless order has multiple outstanding scheduled payments"
                value={stripeScheduledPaymentId}
                onChange={(event) => {
                  setStripeScheduledPaymentId(event.target.value);
                  setStripeReconcilePreview(null);
                  setStripeReconcileMessage("");
                }}
              />
            </div>
            <label
              htmlFor="stripe-amount-adjustment"
              className="flex cursor-pointer items-start gap-3 rounded-lg border bg-background p-3 text-sm transition-colors hover:bg-muted/40"
            >
              <Checkbox
                id="stripe-amount-adjustment"
                className="mt-0.5"
                checked={allowStripeAmountAdjustment}
                onCheckedChange={(checked) =>
                  setAllowStripeAmountAdjustment(checked === true)
                }
              />
              <span className="space-y-1 leading-none">
                <span className="block font-medium leading-snug">
                  Allow amount correction if Stripe differs from scheduled amount
                </span>
                <span className="block leading-snug text-muted-foreground">
                  Requires a reason. Use only when Stripe is the source of truth.
                </span>
              </span>
            </label>
            {allowStripeAmountAdjustment ? (
              <div className="grid gap-2">
                <Label htmlFor="stripe-adjustment-reason">Adjustment reason</Label>
                <Textarea
                  id="stripe-adjustment-reason"
                  placeholder="Example: Stripe settled amount confirmed; scheduled payment had stale migrated amount."
                  value={stripeAdjustmentReason}
                  onChange={(event) => setStripeAdjustmentReason(event.target.value)}
                />
              </div>
            ) : null}
            {stripeReconcileMessage ? (
              <div className="rounded-lg bg-muted p-3 text-sm">
                {stripeReconcileMessage}
              </div>
            ) : null}
            {stripeReconcilePreview ? (
              <div className="space-y-2 rounded-lg border p-3 text-sm">
                <div className="font-medium">Preview</div>
                {stripeReconcilePreview.requiresAmountAdjustment ? (
                  <div className="text-amber-700 dark:text-amber-300">
                    Stripe amount{" "}
                    {formatCurrency(
                      stripeReconcilePreview.stripePaymentIntent?.amount ?? 0,
                    )}{" "}
                    differs from scheduled amount{" "}
                    {formatCurrency(
                      stripeReconcilePreview.scheduledPayment?.amount ?? 0,
                    )}
                    . Check the amount-correction box and enter a reason to continue.
                  </div>
                ) : (
                  <>
                    <div>
                      Scheduled payment: {formatCurrency(stripeReconcilePreview.scheduledPayment?.amount ?? 0)} {"→"} completed
                    </div>
                    <div>
                      Order payment: {stripeReconcilePreview.orderPayment?.currentStatus ?? "—"} →{" "}
                      {stripeReconcilePreview.orderPayment?.newPaymentStatus ?? "—"}
                    </div>
                    {stripeReconcilePreview.amountAdjustment ? (
                      <div className="text-amber-700 dark:text-amber-300">
                        Amount adjustment: {formatCurrency(stripeReconcilePreview.amountAdjustment.from)} {"→"}{" "}
                        {formatCurrency(stripeReconcilePreview.amountAdjustment.to)}
                      </div>
                    ) : null}
                    <div>Payment record: {stripeReconcilePreview.paymentsRecord}</div>
                  </>
                )}
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowStripeReconcile(false)}
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => void handleReconcileStripePayment(true)}
              disabled={isReconcilingStripe || !stripePaymentIntentId.trim()}
            >
              Preview
            </Button>
            <Button
              onClick={() => void handleReconcileStripePayment(false)}
              disabled={
                isReconcilingStripe ||
                !stripePaymentIntentId.trim() ||
                (allowStripeAmountAdjustment && stripeAdjustmentReason.trim().length < 8)
              }
            >
              {isReconcilingStripe ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Reconciling...
                </>
              ) : (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  Reconcile
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Payment Link Dialog ── */}
      <Dialog open={showPaymentLink} onOpenChange={setShowPaymentLink}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Payment Link</DialogTitle>
            <DialogDescription>
              {generatedPaymentLink
                ? `Share this link with the customer to collect payment for order ${selectedOrder?.orderNumber}`
                : `Generate a payment link for order ${selectedOrder?.orderNumber}`}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {generatedPaymentLink ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2 rounded-lg bg-muted p-3">
                  <Input
                    value={generatedPaymentLink}
                    readOnly
                    className="flex-1 text-sm"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={copyPaymentLink}
                    aria-label="Copy payment link"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() =>
                      window.open(
                        generatedPaymentLink!,
                        "_blank",
                        "noopener,noreferrer",
                      )
                    }
                    aria-label="Open payment link"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">
                  The link expires in 30 days.
                </p>
                {selectedOrder?.rawOrder?.paymentMethodDiscount != null && selectedOrder.rawOrder.paymentMethodDiscount > 0 && (
                  <div className="rounded-lg bg-green-50 p-3 text-sm text-green-800 dark:bg-green-950/30 dark:text-green-200">
                    <strong>ACH Discount Applied:</strong> -$
                    {selectedOrder!.rawOrder.paymentMethodDiscount.toFixed(
                      2,
                    )}{" "}
                    (3% off)
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {selectedOrder?.paymentLinkUrl && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800/60 dark:bg-amber-950/30 dark:text-amber-200">
                    <strong>Note:</strong> This order already has a payment
                    link. Generating a new one will replace it.
                  </div>
                )}
                <p className="text-sm text-muted-foreground">
                  Select the payment method for this link. Bank account
                  payments receive a 3% discount.
                </p>
                <div className="space-y-2">
                  {/* Bank Account Option */}
                  <button
                    type="button"
                    className={`flex w-full items-center justify-between rounded-lg border p-4 text-left transition-all ${
                      paymentLinkMethod === "us_bank_account"
                        ? "border-green-500 bg-green-50 dark:border-green-700 dark:bg-green-950/25"
                        : "border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600"
                    }`}
                    onClick={() => setPaymentLinkMethod("us_bank_account")}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`flex h-4 w-4 items-center justify-center rounded-full border-2 ${
                          paymentLinkMethod === "us_bank_account"
                            ? "border-green-500"
                            : "border-gray-300 dark:border-gray-600"
                        }`}
                      >
                        {paymentLinkMethod === "us_bank_account" && (
                          <div className="h-2 w-2 rounded-full bg-green-500" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">
                            Bank Account (ACH)
                          </span>
                          <Badge variant="outline" className={BADGE_TONES.emerald}>
                            Save 3%
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Customer pays via bank transfer
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="font-semibold text-green-600">
                        ${paymentLinkAchTotal.toFixed(2)}
                      </span>
                      {paymentLinkAchDiscountAmount > 0 && (
                        <p className="text-xs text-green-600">
                          -${paymentLinkAchDiscountAmount.toFixed(2)}{" "}
                          discount
                        </p>
                      )}
                    </div>
                  </button>

                  {/* Card Option */}
                  <button
                    type="button"
                    className={`flex w-full items-center justify-between rounded-lg border p-4 text-left transition-all ${
                      paymentLinkMethod === "card"
                        ? "border-primary bg-primary/5"
                        : "border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600"
                    }`}
                    onClick={() => setPaymentLinkMethod("card")}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`flex h-4 w-4 items-center justify-center rounded-full border-2 ${
                          paymentLinkMethod === "card"
                            ? "border-primary"
                            : "border-gray-300 dark:border-gray-600"
                        }`}
                      >
                        {paymentLinkMethod === "card" && (
                          <div className="h-2 w-2 rounded-full bg-primary" />
                        )}
                      </div>
                      <div>
                        <span className="font-medium">
                          Credit/Debit Card
                        </span>
                        <p className="text-xs text-muted-foreground">
                          Customer pays via card
                        </p>
                      </div>
                    </div>
                    <span className="font-semibold">
                      ${paymentLinkAmountBeforeDiscount.toFixed(2)}
                    </span>
                  </button>
                </div>
                <Button
                  onClick={handleGeneratePaymentLink}
                  disabled={isGeneratingLink}
                  className="mt-4 w-full"
                >
                  {isGeneratingLink ? (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Link2 className="mr-2 h-4 w-4" />
                      Generate Payment Link
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowPaymentLink(false);
                setGeneratedPaymentLink(null);
              }}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Split Payment Dialog ── */}
      <Dialog open={showSplitPayment} onOpenChange={setShowSplitPayment}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Split className="h-5 w-5" />
              Split Payment
            </DialogTitle>
            <DialogDescription>
              {splitPaymentResult
                ? `Payment has been split into 3 installments for order ${selectedOrder?.orderNumber}`
                : `Split payment for order ${selectedOrder?.orderNumber} into 3 installments (30, 45, 60 days)`}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {splitPaymentResult ? (
              <div className="space-y-4">
                <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800 dark:border-green-800/60 dark:bg-green-950/30 dark:text-green-200">
                  <strong>Success!</strong> Payment has been split into 3
                  installments.
                </div>
                {selectedSplitPaymentMethod ? (
                  <div className="rounded-lg border bg-muted/40 p-3 text-sm">
                    <p className="font-medium">Scheduled payment method</p>
                    <p className="mt-1 text-muted-foreground">
                      {formatSplitPaymentMethodLabel(selectedSplitPaymentMethod)}
                    </p>
                  </div>
                ) : null}
                <div className="space-y-2">
                  {(splitPaymentResult.installments || []).map(
                    (installment: any) => (
                      <div
                        key={installment.installmentNumber}
                        className="flex items-center justify-between rounded-lg bg-muted p-3"
                      >
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">
                            Installment {installment.installmentNumber} -{" "}
                            {new Date(
                              installment.dueDate,
                            ).toLocaleDateString()}
                          </span>
                        </div>
                        <span className="font-semibold">
							{formatCurrency(installment.amount)}
                        </span>
                      </div>
                    ),
                  )}
                </div>
                <div className="flex items-center justify-between border-t pt-2">
                  <span className="font-medium">Total</span>
                  <span className="font-semibold">
						{formatCurrency(splitPaymentResult.totalAmount || 0)}
                  </span>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    This will create 3 scheduled payments for this order:
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (isEditingInstallments) {
                        setCustomInstallments(
                          buildCustomInstallmentDrafts(splitPaymentPreview),
                        );
                        setIsEditingInstallments(false);
                        return;
                      }

                      setIsEditingInstallments(true);
                    }}
                  >
                    {isEditingInstallments ? 'Reset to Defaults' : 'Edit Dates'}
                  </Button>
                </div>
                {splitPaymentMethods && splitPaymentMethods.length > 0 ? (
                  <div className="space-y-3 rounded-lg border p-3">
                    <div>
                      <p className="text-sm font-medium">Saved payment method</p>
                      <p className="text-xs text-muted-foreground">
                        Select the saved ACH or card method that should be charged for these installments.
                      </p>
                    </div>
                    <Select
                      value={selectedSplitPaymentMethodId}
                      onValueChange={setSelectedSplitPaymentMethodId}
                    >
                      <SelectTrigger aria-label="Split payment method">
                        <SelectValue placeholder="Select saved payment method" />
                      </SelectTrigger>
                      <SelectContent>
                        {splitPaymentMethods.map((paymentMethod) => (
                          <SelectItem key={paymentMethod._id} value={paymentMethod._id}>
                            {formatSplitPaymentMethodLabel(paymentMethod)}
                            {paymentMethod.isDefault ? " (default)" : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedOrderPaymentMethodDiscount > 0 ? (
                      <p className="text-xs text-muted-foreground">
                        {splitPaymentAchSelected
                          ? `ACH discount already included in this order total: ${formatCurrency(selectedOrderPaymentMethodDiscount)}.`
                          : `This order total already includes an ACH discount of ${formatCurrency(selectedOrderPaymentMethodDiscount)}.`}
                      </p>
                    ) : null}
                  </div>
                ) : splitPaymentMethods?.length === 0 && linkedCreditPaymentMethod ? (
                  <div className="space-y-3 rounded-lg border p-3">
                    <div>
                      <p className="text-sm font-medium">Linked credit payment method</p>
                      <p className="text-xs text-muted-foreground">
                        This customer does not have a standalone saved payment-method row, but the credit account is already linked to the method below. The split schedule will keep using it.
                      </p>
                    </div>
                    <div className="rounded-md bg-muted/50 px-3 py-2 text-sm">
                      {linkedCreditPaymentMethod.type === "us_bank_account"
                        ? `${linkedCreditPaymentMethod.bankName || "ACH"}${linkedCreditPaymentMethod.last4 ? ` ••••${linkedCreditPaymentMethod.last4}` : ""}`
                        : `${linkedCreditPaymentMethod.brand || "Card"}${linkedCreditPaymentMethod.last4 ? ` ••••${linkedCreditPaymentMethod.last4}` : ""}`}
                    </div>
                    {selectedOrderPaymentMethodDiscount > 0 ? (
                      <p className="text-xs text-muted-foreground">
                        ACH discount already included in this order total: {formatCurrency(selectedOrderPaymentMethodDiscount)}.
                      </p>
                    ) : null}
                  </div>
                ) : splitPaymentMethods?.length === 0 ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800/60 dark:bg-amber-950/30 dark:text-amber-200">
                    <strong>Heads up:</strong> No saved ACH or card method was found for this customer. The split schedule will keep using the currently linked method if one exists.
                  </div>
                ) : null}
                {splitPaymentPreview.length > 0 && (
                  <>
                    <div className="space-y-2">
                      {customInstallments.map((installment) => (
                        <div
                          key={installment.installmentNumber}
                          className="flex items-center justify-between rounded-lg bg-muted p-3"
                        >
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <span className="text-sm font-medium">
                                Installment{" "}
                                {installment.installmentNumber}
                              </span>
                              {isEditingInstallments ? (
                                <div className="flex items-center gap-2 mt-1">
                                  <Input
                                    type="date"
                                    value={installment.dueDate}
                                    onChange={(e) =>
                                      handleInstallmentDateChange(
                                        installment.installmentNumber,
                                        e.target.value
                                      )
                                    }
                                    className="h-7 text-xs w-auto"
                                  />
                                  <span className="text-xs text-muted-foreground">
                                    ({installment.daysAfterOrder} days after order)
                                  </span>
                                </div>
                              ) : (
                                <p className="text-xs text-muted-foreground">
                                  Due:{" "}
                                  {formatDateOnlyForDisplay(installment.dueDate)}{" "}
                                  ({installment.daysAfterOrder} days after order)
                                </p>
                              )}
                            </div>
                          </div>
                          <span className="font-semibold">
							{formatCurrency(installment.amount)}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center justify-between border-t pt-2">
                      <span className="font-medium">Order Total</span>
                      <span className="font-semibold">
							{formatCurrency(splitPaymentOrderTotal)}
                      </span>
                    </div>
                  </>
                )}
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800/60 dark:bg-amber-950/30 dark:text-amber-200">
                  <strong>Note:</strong> Any existing scheduled payment will
                  be updated to become the first installment. Two additional
                  installments will be created.
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            {splitPaymentResult ? (
              <Button
                onClick={() => {
                  setShowSplitPayment(false);
                  setSplitPaymentResult(null);
                  setSelectedOrder(null);
                }}
              >
                Done
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowSplitPayment(false);
                    setSelectedOrder(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSplitPayment}
                  disabled={isSplittingPayment}
                >
                  {isSplittingPayment ? (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      Splitting...
                    </>
                  ) : (
                    <>
                      <Split className="mr-2 h-4 w-4" />
                      Split Payment
                    </>
                  )}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <FailedPaymentInstallmentConversionDialog
        open={showFailedPaymentConversion}
        onOpenChange={(nextOpen) => {
          setShowFailedPaymentConversion(nextOpen);
          if (!nextOpen) setSelectedOrder(null);
        }}
        order={selectedOrder}
      />
    </>
  );
}
